import {
  ClientMasterData,
  ClientMasterDataChanges,
  MASTER_DATA_FIELDS,
  MasterDataField,
} from "@/data/models/ClientAdministrationRow";

/**
 * The Stammdaten edit of the IRES Admin Client (backlog #38, slice 1). Pure. IRES Cloud's
 * `updateClientAdministration` is the authority (`ClientMasterDataFields.ts`); these rules only give the
 * dialog its messages and decide what is sent.
 */

/** The dialog's form: one text per field, "" for none. */
export type MasterDataForm = Record<MasterDataField, string>;

/** The client types the dialog offers, as the detail labels them. */
export const MASTER_DATA_CLIENT_TYPES = ["IRES", "CLIENT", "OTHER"] as const;

const MAX_LENGTH = 100;
const PREFIX_LENGTH = 4;

/** Why a field cannot be saved, as an i18n key suffix; null when it can. */
export type MasterDataFieldError = "required" | "tooLong" | "prefixLength" | null;

/** The form for the stored values: exactly as stored, so an untouched field is sent back unchanged. */
export function toMasterDataForm(stored: ClientMasterData): MasterDataForm {
  const form = {} as MasterDataForm;
  for (const field of MASTER_DATA_FIELDS) {
    form[field] = stored[field] ?? "";
  }
  return form;
}

/** The value a field is saved as: trimmed, null when empty, the Kürzel in capitals (Taimoor, 2026-10-13). */
export function masterDataValue(field: MasterDataField, text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  return field === "iresProjectNumberPrefix" ? trimmed.toUpperCase() : trimmed;
}

/** Whether a field differs from what was loaded. An untouched field never does, whatever it holds. */
export function isMasterDataFieldChanged(field: MasterDataField, stored: ClientMasterData, form: MasterDataForm): boolean {
  const loaded = stored[field] ?? "";
  if (form[field] === loaded) {
    return false;
  }
  return masterDataValue(field, form[field]) !== stored[field];
}

/** Why a field cannot be saved; checked only for changed fields, as IRES Cloud does. */
export function masterDataFieldError(field: MasterDataField, text: string): MasterDataFieldError {
  const value = masterDataValue(field, text);

  if (value !== null && value.length > MAX_LENGTH) {
    return "tooLong";
  }
  if ((field === "clientName" || field === "clientType") && value === null) {
    return "required";
  }
  if (field === "iresProjectNumberPrefix" && value !== null && value.length !== PREFIX_LENGTH) {
    return "prefixLength";
  }
  return null;
}

/**
 * The request for `updateClientAdministration`: each changed field with the value loaded and the new
 * one; empty when nothing changed.
 */
export function masterDataChanges(stored: ClientMasterData, form: MasterDataForm): ClientMasterDataChanges {
  const changes: ClientMasterDataChanges = {};
  for (const field of MASTER_DATA_FIELDS) {
    if (isMasterDataFieldChanged(field, stored, form)) {
      changes[field] = { expected: stored[field], value: masterDataValue(field, form[field]) };
    }
  }
  return changes;
}

/** Whether the dialog may save: something changed, and every changed field passes its rule. */
export function canSaveMasterData(stored: ClientMasterData, form: MasterDataForm): boolean {
  const changed = MASTER_DATA_FIELDS.filter((field) => isMasterDataFieldChanged(field, stored, form));
  return changed.length > 0 && changed.every((field) => masterDataFieldError(field, form[field]) === null);
}

/** Whether the Kundentyp changes to or from IRES, which widens or narrows the client's access to every client's data. */
export function changesIresType(stored: ClientMasterData, form: MasterDataForm): boolean {
  const before = stored.clientType === "IRES";
  const after = form.clientType === "IRES";
  return isMasterDataFieldChanged("clientType", stored, form) && before !== after;
}

/**
 * The prefix to name when the Kürzel is changed: the one the client's IRES project numbers keep
 * using, since the Kürzel is a label only (Taimoor, 2026-10-13). Null when the Kürzel is unchanged or
 * the client has no project numbers yet.
 */
export function keptProjectNumberPrefix(
  stored: ClientMasterData,
  form: MasterDataForm,
  projectNumberPrefix: string | null | undefined
): string | null {
  return projectNumberPrefix && isMasterDataFieldChanged("iresProjectNumberPrefix", stored, form) ? projectNumberPrefix : null;
}

/** The i18n key of a refusal from IRES Cloud, by its `details.reason`. */
export function masterDataErrorKey(error: unknown): string {
  const failure = (error ?? {}) as { details?: { reason?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  const known = ["CHANGED_MEANWHILE", "PREFIX_TAKEN", "INVALID_VALUE", "NO_CLIENT"];
  return known.includes(reason) ? `admin.clients.masterData.errors.${reason}` : "admin.clients.masterData.errors.FAILED";
}
